import { useCallback, useState, useEffect, useRef } from 'react'
import req from '../api/request'
import {
  FeatureFlags,
  FeatureFlagName,
  UseFeatureFlagsReturn,
} from '../types/FeatureFlags'

/**
 * Hook for fetching and updating feature flags for an account
 * @param accountId - The account ID to fetch/update flags for
 * @returns Object containing flags, loading state, error, and mutation functions
 */
export function useFeatureFlags(
  accountId?: string | null
): UseFeatureFlagsReturn {
  const [flags, setFlags] = useState<FeatureFlags | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<Error | null>(null)
  const [isUpdating, setIsUpdating] = useState(false)
  const abortRef = useRef<AbortController | null>(null)

  const fetchFlags = useCallback(async (): Promise<void> => {
    if (!accountId) {
      setFlags(null)
      return
    }

    setLoading(true)
    setError(null)

    if (abortRef.current) {
      abortRef.current.abort()
    }
    abortRef.current = new AbortController()

    try {
      const response = await req(`/account/${accountId}/features`, {
        method: 'GET',
        signal: abortRef.current.signal,
      })
      const featureFlags =
        response?.data?.feature_flags ?? response?.feature_flags ?? null
      setFlags(featureFlags)
    } catch (err) {
      if ((err as Error)?.name !== 'AbortError') {
        setError(err as Error)
      }
    } finally {
      setLoading(false)
    }
  }, [accountId])

  const updateFlag = useCallback(
    async (name: FeatureFlagName, enabled: boolean): Promise<void> => {
      if (!accountId) {
        throw new Error('Account ID is required to update feature flags')
      }

      setIsUpdating(true)
      setError(null)

      try {
        await req(`/account/${accountId}/features/${name}`, {
          method: 'PUT',
          body: JSON.stringify({ enabled }),
        })

        // Update local state optimistically
        setFlags((prev) => {
          if (!prev) return prev
          return {
            ...prev,
            [name]: enabled,
          }
        })
      } catch (err) {
        setError(err as Error)
        throw err
      } finally {
        setIsUpdating(false)
      }
    },
    [accountId]
  )

  useEffect(() => {
    fetchFlags()

    return () => {
      if (abortRef.current) {
        abortRef.current.abort()
      }
    }
  }, [fetchFlags])

  return {
    flags,
    loading,
    error,
    refetch: fetchFlags,
    updateFlag,
    isUpdating,
  }
}

export default useFeatureFlags
